import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  TERMS_META,
  termsAndConditionsSections,
  type TermsBlock,
  type TermsSection,
} from "@/lib/terms-and-conditions";

const SCROLL_BOTTOM_THRESHOLD_PX = 48;

export interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

function TermsBlockView({ block }: { block: TermsBlock }) {
  if (block.type === "subheading") {
    return <h4 className="mt-4 text-sm font-semibold text-white first:mt-0">{block.text}</h4>;
  }
  if (block.type === "paragraph") {
    return <p className="mt-3 text-sm leading-relaxed text-slate-300">{block.text}</p>;
  }
  if (block.type === "lettered") {
    return (
      <ul className="mt-3 list-none space-y-2 pl-0">
        {block.items.map((item, index) => (
          <li key={`${item.letter}-${index}`} className="flex gap-2 text-sm leading-relaxed text-slate-300">
            <span className="shrink-0 font-medium text-slate-400">{item.letter})</span>
            <span>{item.text}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "bullets") {
    return (
      <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-300">
        {block.items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "caps") {
    return (
      <p className="mt-3 text-xs font-medium uppercase leading-relaxed tracking-wide text-slate-400">{block.text}</p>
    );
  }
  return null;
}

function TermsSectionView({ section }: { section: TermsSection }) {
  return (
    <section className="mt-8 border-b border-slate-700/60 pb-8 first:mt-0 last:border-b-0">
      <h3 className="text-base font-semibold text-white">{section.title}</h3>
      {section.blocks.map((block, index) => (
        <TermsBlockView key={`${section.id}-block-${index}`} block={block} />
      ))}
    </section>
  );
}

export function TermsAndConditionsModal({ isOpen, onAccept, onDecline }: TermsAndConditionsModalProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const checkScrollEnd = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const noScrollNeeded = scrollHeight <= clientHeight + 2;
    const atBottom =
      noScrollNeeded || scrollTop + clientHeight >= scrollHeight - SCROLL_BOTTOM_THRESHOLD_PX;
    if (atBottom) setHasScrolledToBottom(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setHasScrolledToBottom(false);
    const el = scrollRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      checkScrollEnd();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen, checkScrollEnd]);

  useEffect(() => {
    if (!isOpen) return;
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      checkScrollEnd();
    });
    observer.observe(el);
    checkScrollEnd();
    return () => observer.disconnect();
  }, [isOpen, checkScrollEnd]);

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className={cn(
          "flex max-h-[85vh] max-w-3xl flex-col gap-0 overflow-hidden border-indigo-300/20 bg-slate-950/95 p-0 shadow-[0_25px_80px_-45px_rgba(79,70,229,0.75)]",
          "[&>button]:hidden"
        )}
        onPointerDownOutside={event => event.preventDefault()}
        onEscapeKeyDown={event => event.preventDefault()}
      >
        <div className="shrink-0 border-b border-slate-700/80 px-6 pb-4 pt-6">
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="text-xl font-semibold text-white">{TERMS_META.displayTitle}</DialogTitle>
            <DialogDescription className="text-sm text-slate-400">
              {TERMS_META.productOfLine} — Last updated: {TERMS_META.lastUpdated}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div
          ref={scrollRef}
          onScroll={checkScrollEnd}
          className="min-h-0 flex-1 overflow-y-auto border-x border-slate-700/40 bg-slate-950/45 px-6 py-4"
        >
          {termsAndConditionsSections.map(section => (
            <TermsSectionView key={section.id} section={section} />
          ))}
        </div>

        <div className="shrink-0 border-t border-slate-700 px-6 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-xl text-sm text-slate-400">
              You must scroll through and accept these Terms and Conditions to create your account.
            </p>
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" className="border-slate-600 text-slate-200" onClick={onDecline}>
                Decline
              </Button>
              <Button
                type="button"
                className="min-w-[200px]"
                disabled={!hasScrolledToBottom}
                onClick={onAccept}
              >
                {hasScrolledToBottom ? "I Accept" : "Scroll to the end to accept"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
